import * as actions_attachments from 'actions/attachments-actions';
import * as actions_invoices from 'actions/invoices-actions';
import * as actions_quickbooks from 'actions/quickbooks-actions';
import * as actions_quotes from 'actions/quotes-actions';
import * as actions_work_orders from 'actions/work-orders-actions';
import * as toolbox from 'components/common/toolbox';
import AttachmentForm from 'components/attachments/attachment-form/attachment-form';
import Authorize from 'components/common/authorize';
import LineForm from 'components/line-items/line-item-form/line-item-form';
import LineItemsBuilder from 'components/line-items/line-items-builder';
import LineItemsTotals from 'components/line-items/line-items-totals';
import React, { useEffect, useState } from 'react';
import SelectOptions from './select-options';
import _, { set } from 'lodash';
import moment from 'moment';
import { INVOICES, PERMISSIONS, QUOTES, SERVICE_ITEMS, WORKORDERS } from 'components/constants';
import { Checkbox, Ibox, Input, ModalAlert, Select, Spinner } from 'enspire-manager-framework';
import { Table } from 'em-table';
import { confirmDelete } from 'components/common/toolbox';
import { numPanels } from 'components/common/toolbox';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';

const momentDurationFormatSetup = require("moment-duration-format");
momentDurationFormatSetup(moment);

const LineItems = (props) => {
    
    /* Hooks --------------------------*/
    
    const seg = 5;
    const params = useParams();
	const history = useHistory();
    const dispatch = useDispatch();
	const routeLocation = useLocation();
    
    const checkin_time = useSelector((store) => store.workOrders.checkin_time);
    const invoice = useSelector((store) => store.invoices.invoice);
    const profile = useSelector((store) => store.profiles.profile);
    const quickbooksSettings = useSelector((store) => store.quickbooks.quickbooks);
    const quote = useSelector((store) => store.quotes.quote);
    const settings = useSelector(store => store.settings.settings);
    const user_permissions = useSelector((store) => store.users.user_permissions);
    const workOrder = useSelector((store) => store.workOrders.work_order);
    const workOrderServiceItems = useSelector((store) => store.workOrders.work_order_items);
    const workOrders = useSelector((store) => store.workOrders);

    const [selectedOption, setSelectedOption] = useState(0);
    const [highlight, setHighlight] = useState({});
    const [serviceItems, setServiceItems] = useState([]);
    const [depositPercentage, setDepositPercentage] = useState(0);
    const [autoDeposit, setAutoDeposit] = useState(true);

    const isQuote = (props.source == 'quotes');
    const isWorkOrder = (props.source == 'work-orders');
    const isInvoice = (props.source == 'invoices');

    const entity = (isWorkOrder) ? workOrder : (isQuote) ? quote : (isInvoice) ? invoice : null;
    const activeTaxRates = settings?.invoices?.tax_rates?.filter(rate => !rate.hidden) ?? [];
    const taxRate = (entity?.taxRateId) // match taxRateId to QuickBooks ID or index - no tax is empty string '';
        ? activeTaxRates?.find((rate) => rate.rateId == entity?.taxRateId)?.rate ?? activeTaxRates[entity?.taxRateId]?.rate ?? 0 : 0;

    const lineItems = (isQuote)
        ? _.chain(serviceItems).filter((item) => item.option == selectedOption && item.type != 'discount').orderBy(['type'], ['asc']).value()
        : _.chain(serviceItems).filter((item) => item.type != 'discount').orderBy(['type'], ['asc']).value();
    const hasInprogress = !!_.find(lineItems, { checkin: 'inprogress' });
    const hasOnsite = !!_.find(lineItems, { checkin: 'onsite' });
    const hasEnroute = !!_.find(lineItems, { checkin: 'enroute' });

    const externalServiceItems = _.filter(serviceItems, (o) => o.type == 'external');
    const serviceItemsNoExternal = _.filter(serviceItems, (o) => o.type != 'external');
    const isCompleted = (isWorkOrder) ?   (parseInt(entity?.statusId) >= parseInt(WORKORDERS.COMPLETE.id))
        :   (isQuote) ? (parseInt(entity?.statusId) >= parseInt(QUOTES.ACCEPTED.id))
        :   (isInvoice) ? (parseInt(entity?.statusId) >= parseInt(INVOICES.SENT.id)) : false;

    // Permissions ----------------------------------------

    const canView = props.permissions?.canView;
    const canEdit = props.permissions?.canEdit;

    /* Effects -----------------------------------------------------------------------------------------------------------------------*/

    // subscribe to external invoice attachments
    useEffect(() => {
        dispatch(actions_attachments.subAttachments(params.handle, params.work_order_id || params.tab_id, 'work-orders', 'WORK_ORDER', 'external_invoices'));
        return () => {
            const unsubscribe = workOrders.work_order_external_invoices_unsubscribe;
            if (typeof unsubscribe == 'function') unsubscribe();
        };
    }, [params.handle, props.id, props.source]);
    
    useEffect(() => {
        setServiceItems((isWorkOrder) ? workOrderServiceItems 
            : (isQuote) ? quote?.serviceItems ?? [] 
            : (isInvoice) ? invoice?.serviceItems ?? [] : []
        );
    }, [props.source, workOrderServiceItems, quote?.serviceItems, invoice?.serviceItems]);

    // This code is NO GOOD - deletes all service items if external service item is added or removed
    // useEffect(() => {
    //     if (isWorkOrder || isQuote) saveUpdates(serviceItemsNoExternal);
    // }, [JSON.stringify(externalServiceItems)]);
    
    useEffect(() => {
        if (isQuote) setSelectedOption((quote?.approvedOption) ? quote?.approvedOption : 0); 
    }, [quote?.approvedOption]);

    useEffect(() => {
        setDepositPercentage(quote?.depositPercentage || settings?.quotes?.quote_percentage || 0);
        setAutoDeposit(quote?.depositAutoGenerate ?? true);
    }, [quote?.depositAutoGenerate, quote?.depositPercentage]);
    
    /* Handlers ------------------------------------------------------------------------------------------------------------------------------------*/
    
    const handleEdit = (field, updatedItem, newValue) => {
        let hasUpdated = false; // ensure only one gets updated
        let newLineItems = serviceItemsNoExternal.map((item, index) => {
            if (!hasUpdated && actions_work_orders.compareServiceItems(item, updatedItem)) {
                let count = (field != 'count') ? item.count : newValue;
                let price = (field != 'price') ? item.price : newValue;
                hasUpdated = true;
                return { ...item, [field]: newValue, calculatedPrice: price * count };
            } else {
                return item;
            }
        });
        setServiceItems(newLineItems);
        saveUpdates(newLineItems);
    };
    const handleUpdate = (originalItem, updatedItem) => {
        let hasUpdated = false; // ensure only one gets updated
        let newLineItems = serviceItemsNoExternal.map((item, index) => {
            if (!hasUpdated && actions_work_orders.compareServiceItems(item, originalItem)) {
                hasUpdated = true;
                return { ...updatedItem };
            } else {
                return item;
            }
        });
        setServiceItems(newLineItems);
        saveUpdates(newLineItems);
    }
    const handleCheckbox = (updatedItem, e) => {
        e.stopPropagation();
        let newLineItems = serviceItemsNoExternal.map((item, index) => {
            if (item.id == updatedItem.id && ((item.type != 'labor' && item.type != 'travel') || item.description == updatedItem.description)) {
                let taxable = !item.taxable;
                return { ...item, taxable };
            } else {
                return item;
            }
        });
        saveUpdates(newLineItems);
    }
    const handleClick = (item) => {
        if (numPanels(1)) {
            if (item.id == 'external') {
                openExternalInvoiceForm(item);
            } else {
                let location = toolbox.modifyPath(routeLocation?.pathname, seg, 'line-form/' + item.index);
                history.push({ pathname: location });
            }
        }
    }
    const handleAutoDeposit = () => {
        setAutoDeposit(!autoDeposit);
        dispatch(actions_quotes.updateQuote(params.handle, params.quote_id, { depositAutoGenerate: !autoDeposit }));
    }
    const handleDepositPercentage = (e) => {
        const newValue = e.target.value;
        setDepositPercentage(newValue);
        dispatch(actions_quotes.updateQuote(params.handle, params.quote_id, { depositPercentage: newValue }));
    }
    const handleKeyDown = (e) => { 
        if (e.key === 'Enter') e.target.blur(); 
    }

    /* Actions ------------------------------------------------------------------------------------------------------------------------------------*/
    
    const addItems = (items, source) => {
        actions_work_orders.addItemsToLineItems(items, selectedOption, serviceItemsNoExternal, profile, settings, (newLineItems, numberUpdates) => {
            if (numberUpdates) saveUpdates(newLineItems);
            if (source != 'checkin') history.goBack();
        });

    }
    const removeItem = (deletedItem) => {
        let hasDeleted = false;
        let newLineItems = [];
        serviceItemsNoExternal.forEach((item, index) => {
            if (hasDeleted || !actions_work_orders.compareServiceItems(item, deletedItem)) {
                newLineItems.push(item);
            } else {
                hasDeleted = true;
            }
        });
        saveUpdates(newLineItems);
    };
    const removeDiscount = (deletedItemId) => {
        let hasDeleted = false;
        let newLineItems = [];
        serviceItemsNoExternal.forEach((item, index) => {
            if (hasDeleted || item.id != deletedItemId) {
                newLineItems.push(item);
            } else {
                hasDeleted = true;
            }
        });
        saveUpdates(newLineItems);
    };
    const handleReorder = (reorderedItem) => {
        var newLineItems = [];
        lineItems.forEach((item, index) => {
            if (actions_work_orders.compareServiceItems(item, reorderedItem) && index) {
                let prevItem = newLineItems.pop();
                newLineItems.push(reorderedItem);
                newLineItems.push(prevItem);
            } else {
                newLineItems.push(item);
            }
        });
        saveUpdates(newLineItems);

        setHighlight({ id: reorderedItem.id, fade: 'in' });
        setTimeout(() => {
            setHighlight({ id: reorderedItem.id, fade: 'out' });
            setTimeout(() => {
                setHighlight({});
            }, 350);
        }, 350);
    }
    const setTaxRate = (e) => {
        let newTaxRateId = e.target.value;
        if (isWorkOrder) {
            dispatch(actions_work_orders.updateWorkOrder(params.handle, params.work_order_id || params.tab_id, { taxRateId: newTaxRateId}, null, { silent: true }));
        } else if (isQuote) {
            dispatch(actions_quotes.updateQuote(params.handle, params.quote_id, { taxRateId: newTaxRateId}));
        } else if (isInvoice) {
            dispatch(actions_invoices.updateInvoice(params.handle, params.invoice_id, { taxRateId: newTaxRateId}));
        };
        let taxRate = (newTaxRateId) ? activeTaxRates?.find((rate) => rate.rateId == newTaxRateId)?.rate ?? activeTaxRates[newTaxRateId]?.rate ?? 0 : 0;
        saveUpdates(serviceItemsNoExternal, taxRate);
    }
    const saveUpdates = (newLineItems, newTaxRate) => {
        const totals = toolbox.calculatedTotals([...newLineItems, ...externalServiceItems], (isQuote) ? selectedOption : null, newTaxRate ?? taxRate);
    
        if (isWorkOrder) {
            dispatch(actions_work_orders.updateWorkOrderServiceItems(params.handle, params.work_order_id || params.tab_id, newLineItems, totals));
        } else if (isQuote) {
            let update = { serviceItems: newLineItems, ['totals' + selectedOption]: totals };
            dispatch(actions_quotes.updateQuote(params.handle, params.quote_id, update));
        } else if (isInvoice) {
            let update = { serviceItems: [...newLineItems, ...externalServiceItems], totals };
            dispatch(actions_invoices.updateInvoice(params.handle, params.invoice_id, update));
            dispatch(actions_quickbooks.updateQuickbooks({ handle: params.handle, type: 'invoice' }, quickbooksSettings));
        }
    }
    const openBuilder = () => {
        let location = toolbox.modifyPath(routeLocation?.pathname, seg, 'line-items');
        history.push({ pathname: location });
    }
    const openExternalInvoiceForm = (item) => {
        let location = toolbox.modifyPath(routeLocation.pathname, seg, `external-invoice/${item.name}`);
        history.push({ pathname: location });
    };
    const addCheckin = (type) => {
        let item = actions_work_orders.createWorkOrderCheckinEntry(type, checkin_time, settings);
        addItems([item], 'checkin');
    }
    const createNewOption = () => {
        ModalAlert({
            title: 'Are you sure?',
            html: `Are you sure you want to create a new Option based on the current selected Option?`,
            icon: 'question',
            confirm_color: '#8FBC8B',
            confirm_text: 'Yes',
            show_cancel: true,
            callback_success: () => {

                // Use highest option number to increment next option number
                var highestOption = 0;
                quote.options?.forEach((option) => {
                    if (option.index > highestOption) highestOption = option.index;
                });
                const newOptionNumber = parseInt(highestOption) + 1;
        
                // Copy all service items from currently selected option
                var serviceItems = [ ...quote?.serviceItems ];
                var newServiceItems = quote?.serviceItems?.filter((item) => item.option == selectedOption);
                newServiceItems = newServiceItems?.map((item) => ({ ...item, option: newOptionNumber }));
                serviceItems = [...serviceItems, ...newServiceItems];
        
                dispatch(actions_quotes.updateQuote(params.handle, params.quote_id, { options: [ ...quote.options, { 
                    name:`Option #${parseInt(newOptionNumber) + 1}`, 
                    index: newOptionNumber
                }], serviceItems }));

                setSelectedOption(newOptionNumber);
            }
        });
    };

    /* Constants ------------------------------------------------------------------------------------------------------------------------------------*/

    var currentType = '';
    const data = lineItems.map((item, index) => {
        let details = { ...item, _group: 'A', index }
        if (currentType != item.type) {
            details._no_button = true;
            currentType = item.type;
        }
        
        let type = _.find(Object.values(SERVICE_ITEMS), { id: item.type });
        if (type) details = { ...details, _type_position: type.position.toString(), _display: type.name }
        details._noEdit = isCompleted;
        
        // disable editing for External Invoices
        if (item.id == 'external') {
            details._group = (item.id == 'external') ? 'B' : 'A';
            details._disabled_fields = (item.id == 'external') ? ['count', 'price', 'description'] : [];
            if (!isInvoice) details.gear = <button className="btn btn-default btn-xs float-right" title="Edit External Invoice" onClick={ () => openExternalInvoiceForm(item) }>
                <i className="fa-regular fa-gear fa-lg text-muted"></i>
            </button>;
        }
        return details;
    });

    const columns = (numPanels() > 1)
    ?   [
            { name: 'Name', field: 'name', width: 50 },
            { name: 'Qty', field: 'count', text_align: 'center', width: 10, edit: { type: 'text', inputMode: 'decimal', autoSelect: true, callback: handleEdit }},
            { name: 'Price', field: 'price', type: 'number', format: 'usd', text_align: 'right', width: 15, edit: { type: 'usd', autoSelect: true, callback: handleEdit }},
            { name: 'Total', field: 'calculatedPrice', type: 'number', format: 'usd', text_align: 'right', width: 15 },
            { group: 'A', name: 'Tax', field: 'taxable', type: 'checkbox', text_align: 'right', callback: handleCheckbox, width: 10},
            { group: 'B', name: 'Tax', field: 'gear', type: 'jsx', width: 10},
            { name: 'Description', field: 'description', edit: { type: 'textarea', placeholder: '(description)', callback: handleEdit } }, // 2nd Line
        ]
    :   [
            { name: 'Name', field: 'name', width: 30 },
            { name: 'Qty', field: 'count', text_align: 'center', width: 20 },
            { name: 'Price', field: 'price', type: 'number', format: 'usd', text_align: 'right', width: 20 },
            { name: 'Total', field: 'calculatedPrice', type: 'number', format: 'usd', text_align: 'right', width: 20 },
            { group: 'A', name: 'Tax', field: 'taxable', type: 'checkbox', text_align: 'right', callback: handleCheckbox, width: 10},
            { group: 'B', name: 'Tax', field: 'gear', type: 'jsx', width: 10},
            { name: 'Description', field: 'description' }, // 2nd Line
        ]
       
    return (
        <>
            { entity
                ?   <Authorize perm={ PERMISSIONS.WORK_ORDERS_VIEW } or={ canView } show403>
                        <Ibox className="" show_spinner={workOrders.work_order_items_pending} no_fade={false} title={
                            <div>Service Items
                                <span className="float-right mt-n1">
                                    { !isCompleted && isQuote &&
                                        <button className="btn btn-default btn-sm mr-3" onClick={createNewOption}>+ Option</button>
                                    }
                                    <button className="btn btn-primary btn-sm" 
                                        disabled={ isCompleted || (isWorkOrder && !canEdit) } 
                                        onClick={openBuilder}>+ Line Items</button>
                                </span>
                            </div>
                        }>
                            { !isCompleted && isQuote &&
                                <SelectOptions quote={ quote } selectedOption={ selectedOption } setSelectedOption={ setSelectedOption } />
                            }

                            {/* SHOW CHECK-IN LABOR & TRAVEL ----------------------------------------------------------------- */}
                            
                            { !isCompleted &&
                                <div className="row">
                                    { isWorkOrder && checkin_time.inprogress > 60 && !hasInprogress &&
                                        <div className="col-xl-6 animated fadeIn animation-delay-2">
                                            <div className="alert alert-info d-flex justify-content-between align-items-center">
                                                <i className="fa-solid fa-arrow-right-to-bracket fa-rotate-90 fa-2x"></i>
                                                <span>In Progress Labor: &nbsp; <strong>{ moment.duration(checkin_time.inprogress, 'seconds').format("hh:mm", { trim: false }) } hrs</strong></span>
                                                <button className="btn btn-info btn-sm" onClick={() => addCheckin('inprogress')}>Add Labor</button>
                                            </div>
                                        </div>
                                    }
                                    { isWorkOrder && checkin_time.onsite > 60 && !hasOnsite &&
                                        <div className="col-xl-6 animated fadeIn animation-delay-2">
                                            <div className="alert alert-success d-flex justify-content-between align-items-center">
                                                <i className="fas fa-map-marker-alt fa-2x"></i>
                                                <span>Onsite Labor: &nbsp; <strong>{ moment.duration(checkin_time.onsite, 'seconds').format("hh:mm", { trim: false }) } hrs</strong></span>
                                                <button className="btn btn-success btn-sm" onClick={() => addCheckin('onsite')}>Add Labor</button>
                                            </div>
                                        </div>
                                    }
                                    { isWorkOrder && checkin_time.enroute > 60 && !hasEnroute &&
                                        <div className="col-xl-6 animated fadeIn animation-delay-4">
                                            <div className="alert alert-secondary d-flex justify-content-between align-items-center">
                                                <i className="fas fa-route fa-2x"></i>
                                                <span>Enroute Travel: &nbsp; <strong>{ moment.duration(checkin_time.enroute, 'seconds').format("hh:mm", { trim: false }) } hrs</strong></span>
                                                <button className="btn btn-secondary btn-sm" onClick={() => addCheckin('enroute')}>Add Travel</button>
                                            </div>
                                        </div>
                                    }
                                </div>
                            }

                            <Table id="line-items-tab"
                                active_field={'id'}
                                chevron={ numPanels(1) }
                                click_callback={ handleClick }
                                columns={columns}
                                data={data}
                                delete={(numPanels() > 1) ? user_permissions?.WORK_ORDERS_EDIT && !isCompleted : false}
                                group_by={{ fields: ['_type_position'], direction: ['asc'], display: ['_display'] }}
                                highlight={ highlight }
                                highlight_search={true}
                                on_delete={(item) => confirmDelete('Line Item', removeItem.bind(this, item), { soft: true })}
                                on_focus={(props?.setFocused) ? (target) => { props?.setFocused(target) } : undefined }
                                second_line={'description'}
                                second_line_prefix={<i className="fa-solid fa-circle-arrow-right fa-lg mr-1"></i>}
                                show_search={false}
                            />

                            <div className="row mb-4">
                                <div className='col-6 justify-content-start align-items-start'>
                                    <Select
                                        className="mb-3"
                                        label={'Tax Rate:'}
                                        name={'taxRateId'}
                                        onChange={setTaxRate}
                                        value={entity?.taxRateId}
                                        readOnly={isCompleted}
                                    >
                                        <option value="">None</option>
                                        {settings?.invoices?.tax_rates?.filter(rate => !rate.hidden)?.map((rate, index) => {
                                            return <option key={rate.name} value={rate.rateId ?? index}>{rate?.name} - { Math.round(rate.rate * 100) / 100 }%</option>
                                        })}
                                    </Select>

                                    { isQuote && !isCompleted &&
                                        <>
                                            <Input
                                                label="Quote Deposit %"
                                                name="quote_percentage"
                                                className="mb-2"
                                                value={depositPercentage}
                                                type="usd"
                                                onChange={(e) => setDepositPercentage(e.target.value)}
                                                onKeyDown={handleKeyDown}
                                                onBlur={handleDepositPercentage}
                                                // onFocus={(event) => props.setFocused(event.target)}
                                            />
                                            <Checkbox
                                                checked={autoDeposit}
                                                label={'Auto Generate Deposit Invoice on Approval'}
                                                onClick={handleAutoDeposit}
                                            />
                                        </>
                                    }
                                    

                                    {isInvoice && entity?.payments?.length > 0 &&
                                        <>
                                            <p className="mb-1">Payments:</p>
                                            <div className="mt-2 mb-4 p-3 rounded" style={{ backgroundColor: '#eeeeee' }}>
                                                <ul>
                                                    {entity?.payments?.map(payment => (
                                                        <li key={payment.id}>
                                                            <strong>{toolbox.formatNumber(payment.amount, 2, true)}</strong> &nbsp; - &nbsp;
                                                            {`${toolbox.UCFirst(payment.method)}${(payment.checkNumber) ? ' #' + payment.checkNumber : ''}`}, &nbsp;
                                                            {`${moment(payment.date.toDate()).format('MMM Do YYYY')}`}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </>
                                    }
                                </div>
                                <div className='col-6 justify-content-start align-items-end pt-5'>
                                    <LineItemsTotals totals={entity?.['totals' + (isQuote ? selectedOption : '')]} removeDiscount={removeDiscount} />
                                </div>

                            </div>
                        </Ibox>
                    </Authorize>
                :   <Spinner center />
            }

            {/* Integrate Routes under this component -------------------------------------------------- */}

            { params.form == 'line-items' &&
                <LineItemsBuilder addItems={ addItems } lineItems={ data } source={props.source} />
            }
            { params.form == 'line-form' &&
                <LineForm 
                    handleUpdate={ handleUpdate } 
                    lineItem={ data[params.form_id] } 
                    removeItem={ removeItem }
                    source={props.source} 
                    taxRate={ taxRate }
                />
            }
            { params.form == 'external-invoice' &&
                <AttachmentForm
                    expires={ props.expires }
                    field={ 'external_invoices' } 
                    id={ params.work_order_id || params.tab_id } 
                    invoice={ true }
                    prefix={ 'WORK_ORDER' } 
                    singular={ 'work_order' } 
                    source={ 'work-orders' } 
                    store={ 'workOrders' } 
                />
            }
        </>
    );
};

export default LineItems;